/**
 * View As — an ADMIN sees the app exactly as another user does.
 *
 * Built on Better Auth's admin-plugin impersonation: while viewing as someone, the
 * session belongs to the target user and carries `impersonatedBy` = the ADMIN's user id.
 * Every API route resolves its actor from that session, so each screen returns the
 * target's own data with no per-route work.
 *
 * Read-only (product decision 2026-10-19). A write made while viewing as someone would
 * be credited to them in every history and audit table, so every write is refused until
 * the ADMIN exits View As. The only calls a View As session may still make are leaving
 * View As and signing out.
 *
 * Client-safe: no server or database imports — the banner and the Users table share
 * these with the server guards.
 */

import type { NymblRole } from "@/lib/nymbl-roles";

/** Shown by every refused write — surfaces through each mutation's existing error toast. */
export const VIEW_AS_READ_ONLY_MESSAGE = "View As is read-only. Exit View As to make changes.";

/** Better Auth's default; the server config pins it explicitly. */
export const VIEW_AS_SESSION_SECONDS = 60 * 60;

const READ_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** Auth endpoints (path under /api/auth) a View As session may still write to. */
const VIEW_AS_ALLOWED_AUTH_PATHS = new Set(["/admin/stop-impersonating", "/sign-out"]);

/** Only ADMIN may View As. RM holds the other admin-plugin powers but not this one. */
export function canViewAs(role: string | null | undefined): role is Extract<NymblRole, "ADMIN"> {
  return role === "ADMIN";
}

/** True when an app API request must be refused because the session is a View As. */
export function isWriteBlockedByViewAs(
  method: string,
  impersonatedBy: string | null | undefined,
): boolean {
  return Boolean(impersonatedBy) && !READ_METHODS.has(method.toUpperCase());
}

/**
 * True when a Better Auth request must be refused because the session is a View As.
 * `authPath` is the path below `/api/auth` (e.g. `/admin/impersonate-user`). Blocking every
 * other auth write also stops a View As from starting a second View As, changing the
 * target's password, or using an admin target's user-management powers.
 */
export function isAuthCallBlockedByViewAs(
  method: string,
  authPath: string,
  impersonatedBy: string | null | undefined,
): boolean {
  return (
    isWriteBlockedByViewAs(method, impersonatedBy) && !VIEW_AS_ALLOWED_AUTH_PATHS.has(authPath)
  );
}
